import { Skeleton } from "@mui/material";
import { createLink } from "@tanstack/react-router";
import clsx from "clsx";
import { useRef, useState } from "react";

import { Image } from "@/components/Image/Image";
import { Slider } from "@/components/Sliders/Slider";
import { useBannersPlacementQuery } from "@/hooks/useBannersPlacement";
import { useInViewportOnce } from "@/hooks/useInViewportOnce";
import { rudderAnalytics, ContentTrackingEvents } from "@/lib/rudderstack";
import type { PublicBannerData, PublicPlacementData } from "@/types/codegen";
import type { BannersPlacementSlug } from "@/types/enums";

import { ContentBlockDrawer } from "./ContentBlockDrawer";

const ImageLink = createLink(Image);

type BannersPlacementProps = {
  slug: BannersPlacementSlug;
  classNames?: {
    wrapper?: string;
  };
};

export function BannersPlacement({ slug, classNames }: BannersPlacementProps) {
  const { data: placement, isPending } = useBannersPlacementQuery(slug);

  if (isPending) {
    return (
      <div className="ms-main w-full">
        <Skeleton
          variant="text"
          width={150}
          className="text-xl"
          sx={{
            mb: 2,
            bgcolor: "rgba(0, 0, 0, 0.1)",
          }}
        />

        <Slider
          items={[1, 2, 3]}
          slides={{
            perView: "auto",
            spacing: 12,
            origin: "auto",
          }}
          getItemKey={(item) => item}
          slideClassName="min-w-9/10 rounded-2xl"
          renderItem={() => (
            <Skeleton
              data-testid="banner-skeleton"
              variant="rounded"
              className="aspect-[16/9] w-full"
              height="unset"
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.1)",
              }}
            />
          )}
        />
      </div>
    );
  }

  if (!placement || !placement.isEnabled || placement.banners.length === 0) {
    return null;
  }

  return (
    <div className={classNames?.wrapper}>
      <PlacementContent placement={placement} />
    </div>
  );
}

type PlacementContentProps = {
  placement: PublicPlacementData;
};

function PlacementContent({ placement }: PlacementContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<PublicBannerData | null>(
    null,
  );

  const handleBannerClick = (banner: PublicBannerData, position: number) => {
    rudderAnalytics.track(ContentTrackingEvents.CONTENT_BANNER_CLICKED, {
      placement: placement.slug,
      banner_name: banner.name,
      banner_image_url: banner.imageUrl,
      banner_position: position,
    });

    if (banner.actionType !== "DRAWER" || !banner.contentBlock) return;

    setIsDrawerOpen(true);
    setSelectedBanner(banner);
  };

  const handleContentButtonClick = (buttonText: string) => {
    if (!selectedBanner?.contentBlock) return;

    rudderAnalytics.track(ContentTrackingEvents.CONTENT_BLOCK_BUTTON_CLICKED, {
      placement: placement.slug,
      banner_name: selectedBanner.name,
      content_block: selectedBanner.contentBlock.title,
      button_text: buttonText,
    });
  };

  useInViewportOnce(containerRef, {
    callback: () => {
      rudderAnalytics.track(ContentTrackingEvents.CONTENT_PLACEMENT_VIEWED, {
        placement: placement.slug,
        banner_count: placement.banners.length,
      });
    },
  });

  const banners = placement.banners;
  const sectionTitle = placement.sectionTitle;
  const hasMultipleBanners = banners.length > 1;
  const type = placement.displayType;

  return (
    <div ref={containerRef} className="max-w-full">
      {!!sectionTitle && (
        <h6 className="mb-3.5 px-4 text-xl font-bold text-text-title">
          {sectionTitle}
        </h6>
      )}

      <div
        className={clsx("ps-4", {
          "pe-4": !hasMultipleBanners || type === "BANNER",
        })}
      >
        <Slider
          items={banners}
          slides={{
            perView: "auto",
            spacing: 12,
            origin: "auto",
          }}
          getItemKey={(banner, index) => `${banner.id}-${index}`}
          slideClassName={clsx("rounded-2xl", {
            "min-w-full": type === "BANNER",
            "min-w-9/10": type === "WIDE_CAROUSEL",
            "max-w-6/10 min-w-6/10": type === "NARROW_CAROUSEL",
          })}
          renderItem={(banner, index) => {
            const isLinkAction = banner.actionType === "LINK";

            let Component: typeof Image | typeof ImageLink = Image;
            if (isLinkAction) {
              Component = ImageLink;
            }

            return (
              <Component
                src={banner.imageUrl}
                alt={banner.altText}
                className={clsx("w-full object-cover", {
                  "aspect-[16/9]":
                    type === "BANNER" || type === "WIDE_CAROUSEL",
                  "aspect-[16/10]": type === "NARROW_CAROUSEL",
                })}
                fetchPriority={index === 0 ? "high" : "auto"}
                onClick={() => handleBannerClick(banner, index)}
                {...(isLinkAction && {
                  to: banner.linkUrl,
                })}
              />
            );
          }}
        />
      </div>

      <ContentBlockDrawer
        open={isDrawerOpen && !!selectedBanner}
        onClose={() => setIsDrawerOpen(false)}
        contentBlock={selectedBanner?.contentBlock ?? undefined}
        onButtonClick={handleContentButtonClick}
      />
    </div>
  );
}
